/** Abschlussbericht, „Der Nebel lichtet sich“ und die Marken der Zeitleiste. */
import type { OrderKind } from '@friction/engine';
import type { aar as en } from '../en/aar';
import { adj, adjCap, nation, theatre } from '../grammar/de';
import { type NationRef, placePhraseOf, unitShort } from '../names';
import type { Translated } from '../types';

const adjOr = (a: string | null, ending: string): string => (a ? adj(a, ending) : `feindlich${ending}`);
const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const theNation = (n: NationRef | null, c: 'nom' | 'dat'): string =>
  n ? nation(n).the(c) : c === 'nom' ? 'der Feind' : 'dem Feind';

/** Was der mitgelesene Befehl verlangt — Infinitiv am Satzende: „soll bei Dornheim angreifen“. */
const VERB: Record<OrderKind, string> = {
  move: 'marschieren',
  attack: 'angreifen',
  hold: 'halten',
  retreat: 'ausweichen',
  posture: 'die Haltung ändern',
};

export const aar: Translated<typeof en> = {
  'aar.head': 'GENERALSTAB · ABSCHLUSSBERICHT',
  'aar.victory': 'SIEG',
  'aar.defeat': 'NIEDERLAGE',
  'aar.won': ({ reason, days }) =>
    `Nach ${days === 1 ? 'einem Tag' : `${days} Tagen`} ist der Krieg ${reason} gewonnen.`,
  'aar.lost': ({ reason, days }) =>
    `Nach ${days === 1 ? 'einem Tag' : `${days} Tagen`} ist der Krieg ${reason} verloren.`,
  'aar.reason.conquest': 'durch Eroberung, als letztes Heer im Feld,',
  'aar.reason.victory-points': 'nach Siegpunkten',
  'aar.reason.surrender': 'durch die Kapitulation des Feindes',
  'aar.standings': 'Endstand',
  'aar.col.nation': 'Nation',
  'aar.col.vp': 'SP',
  'aar.col.peak': 'Höchst',
  'aar.col.hexes': 'Felder',
  'aar.victor': ' · Sieger',
  'aar.capitulated': ' · kapituliert',
  'aar.record': 'Unsere Bilanz',
  'aar.stat.raised': 'Aufgestellte Divisionen',
  'aar.stat.lost': 'Verlorene Divisionen',
  'aar.stat.destroyed': 'Vernichtete Feinddivisionen',
  'aar.stat.cities': 'Eroberte Städte',
  'aar.stat.read': 'Mitgelesene Feindbefehle',
  'aar.stat.couriers': 'Verlorene Kuriere',
  'aar.stat.peak': 'Größte Ausdehnung (Felder)',
  'aar.stat.vp': 'Gehaltene Siegpunkte',
  'aar.stamp.concluded': 'Abgeschlossen',
  'aar.stamp.armistice': 'Waffenstillstand',
  'aar.stamp.capitulation': 'Kapitulation',
  'aar.fog.kicker': 'Wochenschau · für die Geschichtsschreiber',
  'aar.fog.title': 'Der Nebel lichtet sich',
  'aar.fog.sub':
    'Den Krieg noch einmal sehen — jedes Heer, jeden Kurier, wie es wirklich war. Dann so, wie jeder Stab ihn sah.',
  'aar.save': 'Aufzeichnung sichern',
  'aar.save.hint': 'Den Krieg als .friction-replay-Datei für das Filmarchiv herunterladen',
  'aar.saved': 'Aufzeichnung gesichert',
  'aar.saveFailed': 'Die Aufzeichnung ließ sich nicht sichern.',
  'aar.inspect': 'Die Karte betrachten',
  'aar.menu': 'Zum Hauptmenü',
  'aar.report': 'Abschlussbericht',
  'aar.chart.aria': 'Diagramm',
  'aar.chart.vp': 'Siegpunkte',
  'aar.chart.hexes': 'Gebiet',
  'aar.chart.us': ' (wir)',
  'aar.chart.vpNote':
    'Gehaltene Siegpunkte je Nation. Städte: Kleinstadt 1, Stadt 3, Großstadt 6, Hauptstadt +4.',
  'aar.chart.hexesNote':
    'Gehaltene Felder nach Kenntnis unseres Stabes — Feindgebiet wie zuletzt beobachtet.',
  'aar.chart.decisive': '{pct} aller SP — entscheidend',
  'aar.chart.slider': '{metric} jeder Nation im Verlauf des Krieges',
  'aar.course': 'Der Verlauf des Krieges',

  'rp.title': 'Der Nebel lichtet sich',
  'rp.back': 'Zurück zum Bericht',
  'rp.backArchive': 'Zurück zum Archiv',
  'rp.omniscient': 'Allwissend',
  'rp.omniscient.hint': 'Alles, überall: der Nebel gelichtet (V wechselt)',
  'rp.nation.hint': ({ adj: a }) => `Aus Sicht des ${adj(a, 'en')} Generalstabs — nur, was er wusste`,
  'rp.truth': 'Wahrheit',
  'rp.truth.hint': 'Mit Bleistift eintragen, wo der Feind wirklich stand (T)',
  'rp.whole': 'Die ganze Wahrheit',
  'rp.whole.sub': 'jedes Heer, wo es stand, jeder Befehl auf der Straße',
  'rp.seenBy': ({ adj: a }) => `Aus Sicht des ${adj(a, 'en')} Generalstabs`,
  'rp.seenBy.sub': 'nur, was er wusste',
  'rp.seenBy.truth': ' · blasse Zeichen: wo der Feind wirklich stand',
  'rp.kind.war': 'Kriegsausbruch',
  'rp.kind.capture': 'Stadt genommen',
  'rp.kind.kessel': 'Kessel',
  'rp.kind.intercept': 'Mitgelesen',
  'rp.kind.capitulation': 'Kapitulation',
  'rp.kind.end': 'Waffenstillstand',
  'rp.timeline': 'Zeitleiste des Krieges',
  'rp.backDay': 'Einen Tag zurück (Umschalt+←)',
  'rp.back6': '6 Stunden zurück (←)',
  'rp.play': 'Abspielen (Leertaste)',
  'rp.pause': 'Anhalten (Leertaste)',
  'rp.fwd6': '6 Stunden vor (→)',
  'rp.fwdDay': 'Einen Tag vor (Umschalt+→)',
  'rp.speed': { one: '{n} Kriegsstunde je Sekunde (+/−)', other: '{n} Kriegsstunden je Sekunde (+/−)' },
  'rp.ofDays': 'von {n} Tagen',
  'rp.prev': 'Voriger großer Augenblick ([)',
  'rp.next': 'Nächster großer Augenblick (])',
  'rp.moment': 'Ereignis',
  'rp.key.play': 'Abspielen',
  'rp.key.hours': '6 Std.',
  'rp.key.day': 'Tag',
  'rp.key.view': 'Sicht',
  'rp.verified': 'Aufzeichnung geprüft',
  'rp.diverges': 'Aufzeichnung weicht ab',
  'rp.verified.hint': 'Die Nachrechnung erreicht genau das aufgezeichnete Kriegsende.',
  'rp.diverges.hint':
    'Diese Aufzeichnung stammt aus einer anderen Fassung der Regeln; die Wiedergabe kann vom Geschehen abweichen.',
  'rp.deck': 'Wiedergabesteuerung',
  'rp.torn': 'Der Film ist gerissen',
  'rp.threading': 'Der Film wird eingelegt …',
  'rp.threading.sub': 'Der Krieg wird aus den Befehlen aller Stäbe neu ausgefochten',

  'obs.brand': 'Vom Feldherrnhügel',
  'obs.fallen.stamp': 'Kapitulation',
  'obs.fallen.title': 'Unsere Nation hat die Waffen gestreckt',
  'obs.fallen.ffa':
    'Der Krieg geht ohne uns weiter. Vom Feldherrnhügel lässt er sich bis zum Ende verfolgen — die ganze Wahrheit oder so, wie jeder Stab ihn sieht.',
  'obs.fallen.team':
    'Unsere Verbündeten kämpfen weiter. Die Lagekarte bleibt offen und zeigt, was sie sehen — nicht mehr, damit ihnen kein Wort von uns verrät, was sie nicht wissen können.',
  'obs.observe': 'Den Krieg verfolgen',
  'obs.stay': 'Bei der Lagekarte bleiben',
  'obs.leave': 'Den Krieg verlassen',

  'arc.kicker': 'Wochenschau · Filmarchiv',
  'arc.title': 'Filmarchiv',
  'arc.footer':
    'Aufzeichnungen werden im Abschlussbericht oder in „Der Nebel lichtet sich“ gesichert und bleiben auf diesem Rechner.',
  'arc.load': 'Aufzeichnung laden …',
  'arc.drop': 'oder eine .friction-replay-Datei hierher ziehen',
  'arc.reading': 'Die Rolle wird gelesen …',
  'arc.empty.title': 'Keine Rolle im Projektor',
  'arc.empty.text':
    'Die Aufzeichnung eines Krieges sichern — im Abschlussbericht oder in „Der Nebel lichtet sich“ — und hier laden, um ihn noch einmal zu sehen.',
  'arc.card.head': 'FILMARCHIV · AUFZEICHNUNG',
  'arc.saved': 'Gesichert am {date}',
  'arc.length': { one: '{n} Kriegstag', other: '{n} Kriegstage' },
  'arc.nations': 'Kriegsparteien',
  'arc.victors': 'Sieg',
  'arc.noVictor': 'niemand',
  'arc.commander': 'Befehlshaber',
  'arc.computer': 'Computer',
  'arc.scenario': 'Szenario',
  'arc.rules.same': 'Unter diesen Regeln aufgezeichnet',
  'arc.rules.other':
    'Unter anderen Regeln aufgezeichnet — der Film kann vom Geschehen abweichen; sein Siegel wird es zeigen.',
  'arc.screen': 'Film vorführen',
  'arc.another': 'Eine andere laden',
  'arc.err.tooLarge': 'Diese Datei ist zu groß für eine Kriegsaufzeichnung.',
  'arc.err.notReplay': 'Das ist keine FRICTION-Kriegsaufzeichnung.',
  'arc.err.newer':
    'Diese Aufzeichnung stammt aus einer neueren Fassung von FRICTION. Zum Vorführen bitte das Spiel aktualisieren.',
  'arc.err.version': 'Diese Aufzeichnung hat ein Format, das diese Fassung nicht abspielen kann.',
  'arc.err.corrupt': 'Der Film ist beschädigt und nicht lesbar.',
  'arc.err.invalid': 'Die Aufzeichnung ist fehlerhaft.',
  'arc.err.read': 'Die Datei ließ sich nicht lesen.',

  'mark.war': ({ theatre: name }) => `Krieg ${theatre(name).inside}`,
  'mark.capture': ({ city, to, from, owned }) =>
    `${city ?? 'Eine Stadt'} von ${adjOr(to, 'en')} Truppen genommen${owned ? ` (bisher ${from ? adj(from, '') : 'feindlich'})` : ''}`,
  'mark.kessel': ({ n, adj: a, at }) =>
    `KESSEL — ${n} ${adjOr(a, 'e')} ${n === 1 ? 'Division' : 'Divisionen'} ${placePhraseOf(at, 'de')} eingekesselt`,
  'mark.intercept': ({ reader, method, unit, kind, at }) =>
    `${reader ? adjCap(reader, 'er') : 'Feindlicher'} Stab ${method === 'captured-courier' ? 'fängt einen Kurier ab' : 'entziffert einen Funkspruch'}: ${unitShort(unit, 'de')} soll ${at ? `${placePhraseOf(at, 'de')} ` : ''}${VERB[kind]}`,
  'mark.capitulation': ({ nation: n, by }) =>
    `${capFirst(theNation(n, 'nom'))} ${n && nation(n).gender === 'pl' ? 'kapitulieren' : 'kapituliert'} vor ${theNation(by, 'dat')}`,
  'mark.end': ({ winners }) =>
    `Waffenstillstand — Sieg für ${winners.map((w) => nation(w).the('acc')).join(' und ') || 'niemanden'}`,
};
